import { HttpErrorResponse } from '@angular/common/http';
import { MatDialogConfig } from '@angular/material/dialog';
import { DateTime } from 'luxon';
import { Observable, TimeoutError, throwError } from 'rxjs';
import {
    Covid19Result,
    DecisionType,
    GetDecision,
    GetPatient,
    GetReferral,
    GetReferralBodyPart,
    GetReferralDetails,
    ReferralStatus,
    StrokeQueryType
} from '../api-module/control-centre-api-client';
import { Constants } from '../models/constants';
import { ReferralDetailsInjectData, statusesAllowingAcknowledgement, statusDisplayMapping } from './referrals.model';
import { ViewReferralComponent } from './view-referral/view-referral.component';

export class ReferralsUtility {
    public static getViewReferralMatDialogConfig(data: {
        referralId?: number;
        referralDetails?: GetReferralDetails;
    }): MatDialogConfig<ViewReferralComponent> {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.maxWidth = '100vw';
        dialogConfig.width = '75vw';
        dialogConfig.maxHeight = '95vh';
        dialogConfig.restoreFocus = true;
        dialogConfig.disableClose = true;
        dialogConfig.panelClass = ['referral-dialog', 'draggable-dialog-container', 'resize-dialog'];
        dialogConfig.backdropClass = 'general-dialog-backdrop';
        dialogConfig.autoFocus = false;
        dialogConfig.data = new ReferralDetailsInjectData({
            referralId: data.referralId,
            referralDetails: data.referralDetails
        });
        return dialogConfig;
    }

    private static readonly thromStrokes: Set<StrokeQueryType> = new Set<StrokeQueryType>([
        StrokeQueryType.Thrombectomy,
        StrokeQueryType.Thrombolysis
    ]);

    public static GetUrgencyText(referral: GetReferral): string {
        const strokeQueryType = referral.strokeQueryType;
        if (referral.isTrauma) {
            if (this.IsThromStroke(strokeQueryType)) {
                return 'Trauma, Stroke';
            }
            return 'Trauma';
        } else if (this.IsThromStroke(strokeQueryType)) {
            return 'Stroke';
        } else {
            return 'Urgent';
        }
    }

    public static GetStatusText(status: ReferralStatus): string {
        return statusDisplayMapping.get(status) ?? '';
    }

    public static getLatestDecision(decisions: GetDecision[]): GetDecision | null {
        return decisions?.length > 0
            ? decisions
                  .filter(decision => decision.decisionType === DecisionType.Approved || decision.decisionType === DecisionType.Declined)
                  .sort((left, right) => {
                      const leftDate = DateTime.fromISO(left.createdAt);
                      const rightDate = DateTime.fromISO(right.createdAt);
                      return leftDate > rightDate ? -1 : 1;
                  })[0]
            : null;
    }

    public static DisplayPersonName(person: GetPatient | null | undefined): string {
        if (!person) {
            return '';
        }
        return `${person.firstName} ${person.lastName}`;
    }

    public static DisplayDateTime(dateTime: string, showTime?: boolean): string {
        return DateTime.fromISO(dateTime).toFormat(showTime ? 'dd/MM/yyyy HH:mm:ss' : 'dd/MM/yyyy');
    }

    private static IsThromStroke(strokeQueryType: StrokeQueryType): boolean {
        return this.thromStrokes.has(strokeQueryType);
    }

    public static shouldShowTeamsProtocol(latestDecision: GetDecision | undefined): boolean {
        return !!latestDecision?.notesForReportingRadiologist;
    }

    public static capitalizeFirstLetter(str: string) {
        return str?.length > 0 ? str.charAt(0).toUpperCase() + str.slice(1) : '';
    }

    public static getCovid19Text(covid19Result: Covid19Result | undefined): string {
        // covid19Result comes from API as a string.
        return covid19Result === 'NotAvailable' ? 'Not Available' : covid19Result;
    }

    public static transformToStatusAwaitingAcknowledgement<T extends GetReferral | GetReferralDetails>(referral: T | null) {
        if (referral && statusesAllowingAcknowledgement.includes(referral.status)) {
            referral.status = ReferralStatus.AwaitingAcknowledgement;
        }
        return referral;
    }

    public static getTimeOfDecision(referral: GetReferral | GetReferralDetails): string | undefined {
        if (referral instanceof GetReferralDetails) {
            const finalDecision = ReferralsUtility.getLatestDecision(referral.decisions);
            return finalDecision?.decidedAt;
        } else {
            return referral.timeOfDecision;
        }
    }

    public static getRequestedBodyParts(referral: GetReferral | GetReferralDetails): GetReferralBodyPart[] | undefined {
        if (referral instanceof GetReferral) {
            return referral.requestedBodyParts;
        } else {
            return (
                referral.referralItems?.map(
                    item =>
                        new GetReferralBodyPart({
                            id: item.id,
                            modality: item.modality,
                            bodyPart: item.bodyPart,
                            phaseOfContrast: item.phaseOfContrast
                        })
                ) ?? []
            );
        }
    }

    public static getFinalisedBodyParts(referral: GetReferral | GetReferralDetails): GetReferralBodyPart[] | undefined {
        if (referral instanceof GetReferral) {
            return referral.finalisedBodyParts;
        } else {
            const finalDecision = ReferralsUtility.getLatestDecision(referral.decisions);
            return (
                finalDecision?.decisionItems?.map(
                    item =>
                        new GetReferralBodyPart({
                            id: item.id,
                            modality: item.modality,
                            bodyPart: item.bodyPart,
                            phaseOfContrast: item.phaseOfContrast
                        })
                ) ?? []
            );
        }
    }

    public static getErrorMessage(response: HttpErrorResponse): Observable<never> {
        if (response instanceof TimeoutError) {
            console.error('Request timeout: ', JSON.stringify(response));
            return throwError(Constants.UnableToContactServerMessage);
        }
        if ([403, 400, 404].includes(response.status)) {
            console.error(`Server returned code ${response.status} with body:\n${JSON.stringify(response.error)}`);
            if (response.status === 403) {
                return throwError('You do not have permission to view this referral.');
            }
            if (response.status === 400) {
                // SPA shouldn't submit an invalid form. if it does and server returns error, it's an error scenario.
                const errorMessage =
                    'Something went wrong while getting the referral. Please try again in some time. ' +
                    'If the problem persists, please contact Everlight support team';
                return throwError(errorMessage);
            }
            if (response.status === 404) {
                const errorMessage =
                    'Cannot load the referral details. It may have been deleted. Please try again in some time. ' +
                    'If the problem persists, please contact Everlight support team';
                return throwError(errorMessage);
            }
        }

        console.error('Unable to contact server: ', response.status);
        return throwError(Constants.UnableToContactServerMessage);
    }
}
